import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Image as ImageIcon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg, money, fmtDate } from "@/lib/api";
import { StatusBadge, EmptyState } from "@/components/Bits";
import OutstandingList from "@/components/admin/OutstandingList";

const FILTERS = ["pending", "approved", "rejected", "outstanding", "all"];

export const ScreenshotDialog = ({ payment, onClose, onReview, src }) => {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const [note, setNote] = useState(payment.admin_note || "");
  useEffect(() => {
    let blobUrl;
    api.get(src || `/payments/${payment.id}/screenshot`, { responseType: "blob" })
      .then((r) => { blobUrl = URL.createObjectURL(r.data); setUrl(blobUrl); })
      .catch(() => setFailed(true));
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [payment.id, src]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="screenshot-dialog" className="bg-zinc-950 border-zinc-800 sm:rounded-2xl max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{payment.joiner_name} · {money(payment.amount)}</DialogTitle>
          <DialogDescription>{payment.batch_size > 1 ? `${payment.batch_size} drops: ${payment.drop_title} · ` : ""}{payment.reference ? `Ref ${payment.reference} · ` : ""}{fmtDate(payment.created_at)}</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl bg-zinc-900 min-h-[300px] max-h-[55vh] grid place-items-center overflow-auto">
          {url ? <img src={url} alt="Payment proof" data-testid="screenshot-image" className="max-h-[55vh] object-contain" />
            : failed ? <span className="text-sm text-red-400">Couldn't load screenshot</span>
            : <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />}
        </div>
        {payment.note && <div className="text-sm text-zinc-400">Joiner note: {payment.note}</div>}
        <Input data-testid="review-note-input" placeholder="Note to joiner (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="bg-zinc-900" />
        <div className="grid grid-cols-2 gap-2">
          <Button data-testid="reject-payment-button" variant="outline" onClick={() => onReview(payment, "rejected", note)} className="rounded-xl border-red-500/40 text-red-300 bg-transparent hover:bg-red-500/10">Reject</Button>
          <Button data-testid="approve-payment-button" onClick={() => onReview(payment, "approved", note)} className="rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white">Approve</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const groupBatches = (list) => {
  const out = [];
  const seen = {};
  for (const p of list) {
    if (!p.batch_id) { out.push(p); continue; }
    if (seen[p.batch_id]) {
      const g = seen[p.batch_id];
      g.amount = Math.round((g.amount + p.amount) * 100) / 100;
      g.drop_title = `${g.drop_title} + ${p.drop_title || "General"}`;
      g.batch_size += 1;
      g.kind = g.kind === p.kind ? g.kind : "mixed";
      continue;
    }
    seen[p.batch_id] = { ...p, batch_size: 1, drop_title: p.drop_title || "General" };
    out.push(seen[p.batch_id]);
  }
  return out;
};

export default function PaymentsAdmin() {
  const [filter, setFilter] = useState("pending");
  const [payments, setPayments] = useState([]);
  const [viewing, setViewing] = useState(null);
  const load = useCallback(() => {
    if (filter === "outstanding") return;
    api.get("/payments", { params: filter === "all" ? {} : { status: filter } }).then((r) => setPayments(groupBatches(r.data))).catch(() => {});
  }, [filter]);
  useEffect(() => {
    load();
  }, [load]);

  const review = async (p, status, admin_note) => {
    try {
      await api.patch(`/payments/${p.id}`, { status, admin_note });
      toast.success(p.batch_size > 1 ? `Payment ${status} for ${p.batch_size} drops` : `Payment ${status}`);
      setViewing(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <div className="flex gap-1 rounded-full border border-white/5 bg-zinc-900 p-1 w-fit max-w-full overflow-x-auto">
        {FILTERS.map((f) => (
          <button key={f} data-testid={`payments-filter-${f}`} onClick={() => setFilter(f)}
            className={`px-3 sm:px-4 py-1.5 rounded-full text-sm font-medium capitalize whitespace-nowrap transition-colors duration-200 ${filter === f ? "bg-white text-zinc-950" : "text-zinc-400 hover:text-white"}`}>
            {f}
          </button>
        ))}
      </div>
      {filter === "outstanding" ? <OutstandingList /> : (
      <div className="mt-5 space-y-2" data-testid="admin-payments-list">
        {payments.length === 0 && <EmptyState title={`No ${filter === "all" ? "" : filter} payments`} />}
        {payments.map((p) => (
          <div key={p.id} data-testid={`admin-payment-${p.id}`} className="flex flex-wrap items-center gap-3 sm:gap-4 rounded-xl border border-white/5 bg-zinc-900/60 px-4 py-3">
            <div className="min-w-0 flex-1">
              <div className="font-semibold flex items-center gap-2">{p.joiner_name}{p.kind !== "regular" && <span data-testid={`admin-payment-special-tag-${p.id}`} className="rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-200">{p.kind === "mixed" ? "Incl. special" : "Special"}</span>}{p.batch_size > 1 && <span data-testid={`admin-payment-batch-tag-${p.id}`} className="rounded-full bg-violet-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-violet-200">{p.batch_size} drops</span>}</div>
              <div className="text-xs text-zinc-500 truncate">{p.drop_title || "General"}{p.methods?.length ? ` · ${p.methods.map((m) => m.split(":")[0]).join(", ")}` : ""}{p.reference ? ` · ref ${p.reference}` : ""} · {fmtDate(p.created_at)}</div>
            </div>
            <span className="font-mono font-bold">{money(p.amount)}</span>
            <StatusBadge status={p.status} />
            <Button size="sm" variant="outline" data-testid={`view-payment-${p.id}`} onClick={() => setViewing(p)} className="rounded-lg border-zinc-700 bg-transparent">
              <ImageIcon className="h-4 w-4 mr-1.5" /> Review
            </Button>
          </div>
        ))}
      </div>
      )}
      {viewing && <ScreenshotDialog payment={viewing} onClose={() => setViewing(null)} onReview={review} />}
    </div>
  );
}
